import React, { ReactNode, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor } from '../../App.styles';
import { StepperContext } from '../../contexts';
import { useMetrics } from '../../hooks';
import {
  isAmazonHost,
  metricEvents,
  MetricProperty,
  RingtercomFeature,
  TestId,
} from '../../utils';
import { Button } from '../index';
import {
  Cards,
  Content,
  Header,
  StyledCard,
  TagContainer,
} from './RingtercomCards.styles';

export interface RingtercomCard {
  deviceName: React.ReactNode;
  deviceDescription: React.ReactNode;
  deviceIcon?: string;
  tag?: React.ReactNode;
  features: React.ReactNode[];
  shopUrl: string;
  deviceType: RingtercomFeature;
  compatible: boolean;
  children?: ReactNode;
  videoSupport: boolean;
}

interface RingtercomCardsProps {
  cards: RingtercomCard[];
}

const RingtercomCards = ({ cards }: RingtercomCardsProps) => {
  const { t } = useTranslation('common');
  const { track } = useMetrics();
  const { stepperData } = useContext(StepperContext);
  const { brand, model, terminals, multiDoor, isParallel, variant, hasVideo } =
    stepperData;

  const booleanToString = (bool: boolean) => (bool ? 'true' : 'false');

  const commonMetricProperties = {
    brand,
    model: model?.model_name,
    [MetricProperty.terminals]: terminals?.map((el) => el).join(','),
    [MetricProperty.multiDoor]:
      multiDoor === undefined ? undefined : booleanToString(multiDoor),
    [MetricProperty.parallel]:
      isParallel === undefined ? undefined : booleanToString(isParallel),
    [MetricProperty.backplate]: variant?.backplate_number,
    [MetricProperty.supportVideo]:
      hasVideo === undefined ? undefined : booleanToString(hasVideo),
  };

  useEffect(() => {
    track(metricEvents.ringtercomCardsLoaded, {
      cardsShown: cards.length,
      cardTypes: cards.map((card) => card.deviceType),
      compatibility: cards.map((card) => ({
        ringtercom: card.deviceType,
        compatible: card.compatible,
        videoSupport: card.videoSupport,
      })),
      ...commonMetricProperties,
    });
  }, []);

  return (
    <Cards>
      <div className="row gap-3 gx-0">
        {cards.map(
          (
            {
              tag,
              deviceName,
              deviceDescription,
              features,
              shopUrl,
              deviceIcon,
              deviceType,
              compatible,
              children,
            },
            index
          ) => (
            <div key={index} className="card-column col">
              <StyledCard
                data-testid={TestId.RingtercomCard}
                tabIndex={0}
                className={[
                  compatible ? '' : 'disabled',
                  'd-flex',
                  'flex-column',
                ].join(' ')}
              >
                <Header recommended={!index}>
                  <TagContainer>
                    {!isAmazonHost() && deviceIcon && (
                      <img alt="" className="icon" src={deviceIcon}></img>
                    )}
                    {tag}
                  </TagContainer>
                  <div className="text">
                    <h4>{deviceName}</h4>
                    <p className="device-description">{deviceDescription}</p>
                  </div>
                </Header>
                <Content className="flex-grow-1">
                  <div className="text flex-grow-1">
                    <p className="fw-bolder">
                      {t('results.success.ringtercomCard.features')}
                    </p>

                    <ul>
                      {features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  {children}

                  <Anchor
                    noUnderline
                    href={shopUrl}
                    onClick={() =>
                      track(metricEvents.shopNowBtn, {
                        shopUrl,
                        cardType: deviceType,
                        ...commonMetricProperties,
                      })
                    }
                  >
                    <Button
                      secondary={!!index}
                      className={`align-self-center ${
                        isAmazonHost() && 'rounded-pill'
                      }`}
                      type="button"
                      data-testid={TestId.ButtonShopNow}
                    >
                      {t('results.success.ringtercomCard.shopNowBtn')}
                    </Button>
                  </Anchor>
                </Content>
              </StyledCard>
            </div>
          )
        )}
      </div>
    </Cards>
  );
};

export default RingtercomCards;
